import { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendDown, faArrowTrendUp, faBolt, faBook, faChartBar, faChartLine,
  faCircleCheck, faCircleExclamation, faClockRotateLeft, faCode, faCodeBranch,
  faCodeMerge, faComment, faLayerGroup, faRotate, faSpinner, faTrophy, faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { faJira, faTrello, faConfluence, faSlack, faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import * as historyStore from '@/services/analysis-history-store';
import { type AnalysisSnapshotMeta, type AnalysisMode, ANALYSIS_MODE_LABELS } from '@/services/analysis-history-store';

function timeAgo(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

function fmtDur(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

const CC = ['#6366f1','#10b981','#3b82f6','#f59e0b','#ec4899','#8b5cf6','#06b6d4'];

const MM: Record<string,{icon:React.ReactNode;colour:string;bg:string}> = {
  combined:      { icon:<FontAwesomeIcon icon={faCodeMerge}/>,  colour:'text-orange-500', bg:'bg-orange-500/10' },
  cross_project: { icon:<FontAwesomeIcon icon={faLayerGroup}/>, colour:'text-emerald-500',bg:'bg-emerald-500/10'},
  jira:          { icon:<FontAwesomeIcon icon={faJira}/>,       colour:'text-blue-500',   bg:'bg-blue-500/10'  },
  linear:        { icon:<FontAwesomeIcon icon={faBolt}/>,       colour:'text-indigo-500', bg:'bg-indigo-500/10'},
  trello:        { icon:<FontAwesomeIcon icon={faTrello}/>,     colour:'text-sky-500',    bg:'bg-sky-500/10'   },
  git:           { icon:<FontAwesomeIcon icon={faCodeBranch}/>, colour:'text-purple-500', bg:'bg-purple-500/10'},
  confluence:    { icon:<FontAwesomeIcon icon={faConfluence}/>, colour:'text-cyan-500',   bg:'bg-cyan-500/10'  },
  slack:         { icon:<FontAwesomeIcon icon={faSlack}/>,       colour:'text-rose-500',   bg:'bg-rose-500/10'  },
  teams:         { icon:<FontAwesomeIcon icon={faMicrosoft}/>,   colour:'text-violet-500', bg:'bg-violet-500/10'},
};

function Card({icon,label,value,sub,colour}:{icon:React.ReactNode;label:string;value:string|number;sub?:string;colour:string}) {
  return (
    <div className="bg-white/60 dark:bg-white/[0.04] backdrop-blur-xl border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-5">
      <div className="flex items-start justify-between mb-3">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        <span className={`${colour} opacity-75`}>{icon}</span>
      </div>
      <div className={`text-3xl font-extrabold tracking-tight ${colour}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Row({meta,onReopen}:{meta:AnalysisSnapshotMeta;onReopen?:(id:string)=>void}) {
  const m = MM[meta.mode] ?? MM.git;
  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] rounded-xl group transition-colors">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm flex-shrink-0 ${m.bg} ${m.colour}`}>{m.icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{meta.title}</p>
        <p className="text-xs text-slate-400 dark:text-slate-500">
          {ANALYSIS_MODE_LABELS[meta.mode as AnalysisMode]} · {timeAgo(meta.timestamp)}
          {meta.summary.commitCount>0&&` · ${meta.summary.commitCount.toLocaleString()} commits`}
          {meta.summary.eventCount>0&&` · ${meta.summary.eventCount.toLocaleString()} events`}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {meta.success
          ? <FontAwesomeIcon icon={faCircleCheck} className="h-3.5 w-3.5 text-emerald-400"/>
          : <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5 text-red-400"/>}
        {onReopen && (
          <button onClick={()=>onReopen(meta.id)}
            className="opacity-0 group-hover:opacity-100 text-xs px-2.5 py-1 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/20 transition-all">
            Reopen
          </button>
        )}
      </div>
    </div>
  );
}

interface Props { isDarkMode:boolean; onReopenSnapshot?:(id:string)=>void; }

export function DashboardView({ isDarkMode, onReopenSnapshot }: Props) {
  const [snaps, setSnaps] = useState<AnalysisSnapshotMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string|null>(null);

  useEffect(()=>{
    let cancel=false;
    setLoading(true);
    historyStore.getAllSnapshotMeta()
      .then(d=>{ if(!cancel){setSnaps(d);setLoading(false);} })
      .catch(e=>{ if(!cancel){setErr(String(e));setLoading(false);} });
    return ()=>{ cancel=true; };
  },[]);

  const stats = useMemo(()=>{
    if(!snaps.length) return null;
    const ok=snaps.filter(s=>s.success);
    const totalCommits=snaps.reduce((s,a)=>s+(a.summary.commitCount||0),0);
    const totalEvents =snaps.reduce((s,a)=>s+(a.summary.eventCount||0),0);
    const peakAuthors =Math.max(...snaps.map(s=>s.summary.authorCount||0),0);
    const avgDur=ok.length?Math.round(ok.reduce((s,a)=>s+a.durationMs,0)/ok.length):0;
    const byMode:Record<string,number>={};
    snaps.forEach(s=>{byMode[s.mode]=(byMode[s.mode]||0)+1;});
    const byWeek:Record<string,{week:string;count:number;commits:number;events:number}>={};
    snaps.forEach(s=>{
      const d=new Date(s.timestamp);
      const ws=new Date(d); ws.setDate(d.getDate()-d.getDay());
      const k=ws.toISOString().slice(0,10);
      if(!byWeek[k]) byWeek[k]={week:k,count:0,commits:0,events:0};
      byWeek[k].count+=1; byWeek[k].commits+=s.summary.commitCount||0; byWeek[k].events+=s.summary.eventCount||0;
    });
    const weekly=Object.values(byWeek).sort((a,b)=>a.week.localeCompare(b.week)).slice(-12)
      .map(w=>{
        const start=new Date(w.week);
        const end=new Date(start); end.setDate(start.getDate()+6);
        const sD=start.getDate();
        const eD=end.getDate();
        const eMon=end.toLocaleDateString('en-AU',{month:'short'});
        // Same month → "15–21 Feb", cross-month → "27 Jan–2 Feb"
        if(start.getMonth()===end.getMonth()){
          return {...w, label:`${sD}–${eD} ${eMon}`};
        }
        const sMon=start.toLocaleDateString('en-AU',{month:'short'});
        return {...w, label:`${sD} ${sMon}–${eD} ${eMon}`};
      });
    const pie=Object.entries(byMode).map(([mode,count],i)=>({
      name:ANALYSIS_MODE_LABELS[mode as AnalysisMode]||mode, value:count, fill:CC[i%CC.length],
    }));
    const r5=ok.slice(0,5); const p5=ok.slice(5,10);
    const rAvg=r5.length?r5.reduce((s,a)=>s+(a.summary.commitCount||0),0)/r5.length:0;
    const pAvg=p5.length?p5.reduce((s,a)=>s+(a.summary.commitCount||0),0)/p5.length:0;
    const trend=pAvg>0?((rAvg-pAvg)/pAvg)*100:0;
    return {total:snaps.length,ok:ok.length,rate:Math.round((ok.length/snaps.length)*100),
      totalCommits,totalEvents,peakAuthors,avgDur,byMode,weekly,pie,trend,rAvg,pAvg};
  },[snaps]);

  const recent=snaps.slice(0,10);
  const activeModes=useMemo(()=>Array.from(new Set(snaps.map(s=>s.mode))),[snaps]);
  const tt=isDarkMode
    ?{backgroundColor:'#1e293b',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'12px',color:'#e2e8f0',fontSize:12}
    :{backgroundColor:'#fff',border:'1px solid rgba(0,0,0,0.06)',borderRadius:'12px',color:'#334155',fontSize:12};

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-transparent">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-black/[0.06] dark:border-white/[0.06] px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-500/10 flex items-center justify-center">
              <FontAwesomeIcon icon={faChartLine} className="h-4 w-4 text-indigo-500 dark:text-indigo-400"/>
            </div>
            <div>
              <h1 className="text-base font-semibold text-gray-900 dark:text-white leading-tight">Team Dashboard</h1>
              <p className="text-xs text-slate-400 dark:text-slate-500">Performance across all your analyses</p>
            </div>
          </div>
          {stats && (
            <span className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
              <FontAwesomeIcon icon={faClockRotateLeft} className="h-3.5 w-3.5"/>
              {stats.total} {stats.total===1?'analysis':'analyses'} saved
            </span>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {loading && (
          <div className="flex items-center justify-center py-24 gap-3 text-slate-400">
            <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin"/>
            <span className="text-sm">Loading your analysis history…</span>
          </div>
        )}
        {err && (
          <div className="flex items-center gap-2 text-red-500 text-sm py-8 justify-center">
            <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4"/>
            Failed to load history: {err}
          </div>
        )}

        {!loading && !err && !snaps.length && (
          <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
            <div className="w-20 h-20 rounded-2xl bg-indigo-500/10 flex items-center justify-center">
              <FontAwesomeIcon icon={faChartLine} className="h-8 w-8 text-indigo-400"/>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-2">No analyses yet</h2>
              <p className="text-sm text-slate-400 dark:text-slate-500 max-w-sm">
                Run your first analysis — connect a platform in Settings and start tracking your team's activity. Your dashboard will populate automatically.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-2 max-w-xs w-full">
              {[
                {icon:<FontAwesomeIcon icon={faJira}/>,   label:'Jira',      c:'text-blue-500 bg-blue-500/10'},
                {icon:<FontAwesomeIcon icon={faBook}/>,   label:'Confluence',c:'text-cyan-500 bg-cyan-500/10'},
                {icon:<FontAwesomeIcon icon={faCodeBranch}/>,label:'Git',    c:'text-purple-500 bg-purple-500/10'},
                {icon:<FontAwesomeIcon icon={faBolt}/>,   label:'Linear',    c:'text-indigo-500 bg-indigo-500/10'},
                {icon:<FontAwesomeIcon icon={faTrello}/>, label:'Trello',    c:'text-sky-500 bg-sky-500/10'},
                {icon:<FontAwesomeIcon icon={faComment}/>,label:'Chat soon', c:'text-rose-400 bg-rose-500/10'},
              ].map(({icon,label,c})=>(
                <div key={label} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl text-xs font-medium ${c}`}>
                  <span className="text-lg">{icon}</span>{label}
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && !err && stats && (<>
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card icon={<FontAwesomeIcon icon={faChartBar} className="h-4 w-4"/>} label="Analyses Run"
              value={stats.total} sub={`${stats.rate}% success rate`} colour="text-indigo-500 dark:text-indigo-400"/>
            <Card icon={<FontAwesomeIcon icon={faCode} className="h-4 w-4"/>} label="Total Commits"
              value={stats.totalCommits.toLocaleString()} sub={`${stats.totalEvents.toLocaleString()} total events`}
              colour="text-violet-500 dark:text-violet-400"/>
            <Card icon={<FontAwesomeIcon icon={faUsers} className="h-4 w-4"/>} label="Peak Contributors"
              value={stats.peakAuthors} sub="highest in a single run" colour="text-emerald-500 dark:text-emerald-400"/>
            <Card icon={<FontAwesomeIcon icon={faRotate} className="h-4 w-4"/>} label="Avg Analysis Time"
              value={fmtDur(stats.avgDur)} sub={`${stats.ok} successful runs`} colour="text-amber-500 dark:text-amber-400"/>
          </div>

          {/* Trend callout */}
          {stats.total>=10 && (
            <div className={`flex items-center gap-3 px-5 py-4 rounded-2xl border ${
              stats.trend>=0
                ?'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20'
                :'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20'}`}>
              <FontAwesomeIcon icon={stats.trend>=0?faArrowTrendUp:faArrowTrendDown}
                className={`h-5 w-5 flex-shrink-0 ${stats.trend>=0?'text-emerald-500':'text-amber-500'}`}/>
              <div>
                <p className={`text-sm font-semibold ${stats.trend>=0?'text-emerald-700 dark:text-emerald-300':'text-amber-700 dark:text-amber-300'}`}>
                  {stats.trend>=0?'Team momentum is building':'Commit volume dipped recently'}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Recent 5 runs averaged {Math.round(stats.rAvg).toLocaleString()} commits vs {Math.round(stats.pAvg).toLocaleString()} previously
                  {' '}({stats.trend>=0?'+':''}{stats.trend.toFixed(1)}%)
                </p>
              </div>
            </div>
          )}

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white/60 dark:bg-white/[0.04] backdrop-blur-xl border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <FontAwesomeIcon icon={faChartLine} className="h-3.5 w-3.5 text-indigo-400"/>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Analysis Activity</h3>
                <span className="text-xs text-slate-400 ml-auto">last 12 weeks</span>
              </div>
              {stats.weekly.length>1?(
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={stats.weekly} margin={{top:4,right:4,bottom:0,left:-24}}>
                    <defs>
                      <linearGradient id="dg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6366f1" stopOpacity={0.3}/>
                        <stop offset="100%" stopColor="#6366f1" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.06)'}/>
                    <XAxis dataKey="label" tick={{fontSize:10,fill:isDarkMode?'#64748b':'#94a3b8'}} tickLine={false} axisLine={false}/>
                    <YAxis tick={{fontSize:10,fill:isDarkMode?'#64748b':'#94a3b8'}} tickLine={false} axisLine={false}/>
                    <Tooltip contentStyle={tt}/>
                    <Area type="monotone" dataKey="count" name="Analyses" stroke="#6366f1" fill="url(#dg)" strokeWidth={2} dot={false}/>
                  </AreaChart>
                </ResponsiveContainer>
              ):(
                <div className="h-[180px] flex items-center justify-center text-sm text-slate-400">
                  Run more analyses across multiple weeks to see your trend
                </div>
              )}
            </div>

            <div className="bg-white/60 dark:bg-white/[0.04] backdrop-blur-xl border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <FontAwesomeIcon icon={faLayerGroup} className="h-3.5 w-3.5 text-violet-400"/>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">By Platform</h3>
              </div>
              <ResponsiveContainer width="100%" height={130}>
                <PieChart>
                  <Pie data={stats.pie} cx="50%" cy="50%" innerRadius={34} outerRadius={54} paddingAngle={3} dataKey="value">
                    {stats.pie.map((_,i)=><Cell key={i} fill={CC[i%CC.length]}/>)}
                  </Pie>
                  <Tooltip contentStyle={tt}/>
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {stats.pie.map((d,i)=>(
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{background:CC[i%CC.length]}}/>
                      <span className="text-slate-600 dark:text-slate-300 truncate max-w-[120px]">{d.name}</span>
                    </div>
                    <span className="text-slate-400 font-medium">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Commits & events bar chart */}
          {stats.weekly.some(w=>w.commits>0||w.events>0) && (
            <div className="bg-white/60 dark:bg-white/[0.04] backdrop-blur-xl border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <FontAwesomeIcon icon={faCode} className="h-3.5 w-3.5 text-violet-400"/>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Commits &amp; Events Over Time</h3>
              </div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={stats.weekly} margin={{top:4,right:4,bottom:0,left:-24}} barSize={14}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.06)'}/>
                  <XAxis dataKey="label" tick={{fontSize:10,fill:isDarkMode?'#64748b':'#94a3b8'}} tickLine={false} axisLine={false}/>
                  <YAxis tick={{fontSize:10,fill:isDarkMode?'#64748b':'#94a3b8'}} tickLine={false} axisLine={false}/>
                  <Tooltip contentStyle={tt}/>
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:11}}/>
                  <Bar dataKey="commits" name="Commits" fill="#8b5cf6" radius={[3,3,0,0]}/>
                  <Bar dataKey="events"  name="Events"  fill="#06b6d4" radius={[3,3,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Bottom row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white/60 dark:bg-white/[0.04] backdrop-blur-xl border border-black/[0.06] dark:border-white/[0.06] rounded-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-black/[0.04] dark:border-white/[0.04]">
                <FontAwesomeIcon icon={faClockRotateLeft} className="h-3.5 w-3.5 text-slate-400"/>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Recent Analyses</h3>
              </div>
              <div className="p-2">
                {recent.map(m=><Row key={m.id} meta={m} onReopen={onReopenSnapshot}/>)}
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white/60 dark:bg-white/[0.04] backdrop-blur-xl border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <FontAwesomeIcon icon={faTrophy} className="h-3.5 w-3.5 text-amber-400"/>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Platforms Used</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {activeModes.map(mode=>{
                    const m=MM[mode]??MM.git;
                    return (
                      <span key={mode} className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg ${m.bg} ${m.colour}`}>
                        <span>{m.icon}</span>
                        {ANALYSIS_MODE_LABELS[mode as AnalysisMode]||mode}
                        <span className="opacity-50">({stats.byMode[mode]})</span>
                      </span>
                    );
                  })}
                  <span className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-rose-500/10 text-rose-400 opacity-60">
                    <FontAwesomeIcon icon={faComment}/> Chat soon
                  </span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-indigo-500/10 to-violet-500/10 border border-indigo-500/20 dark:border-indigo-400/20 rounded-2xl p-5">
                <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 mb-1.5">Not just for dev teams</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  Connect Jira, Confluence, Monday, Trello, or Linear — VeriGit works for ops, marketing, content, and any other team. Missing a platform?{' '}
                  <a href="https://github.com/moderniselife/VeriGit/issues" target="_blank" rel="noopener noreferrer"
                    className="text-indigo-500 dark:text-indigo-400 underline underline-offset-2">Request it on GitHub.</a>
                </p>
              </div>
            </div>
          </div>
        </>)}
      </div>
    </div>
  );
}
